import { Inject, Injectable } from '@angular/core';
import {
  Observable,
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  of,
  pluck,
  publishReplay,
  refCount,
  scan,
  switchMap,
  tap,
} from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  JOURNAL_FACTORY,
  JOURNAL_SERVICE,
  JournalFactory as IJournalFactory,
  JournalService,
  JOURNAL_STATE_STORE,
  JournalStateStore as JournalStateManager,
  JournalState,
  QueryRequestSourceData,
  JournalAdapter,
  QueryRequestConfig,
  JournalQueryResult,
  ReduxAction,
  JournalRecord,
  ColumnParams,
} from '../common';
import {
  JournalActionTypes,
  JournalActions,
  RefreshQueryResultAction,
  SetJournalCodeAction,
  SetJournalStaticSettingsAction,
  SetQueryResultAction,
  SetSearchTextAction,
} from '../store';
import { Diff, DiffWithState, FieldDiff, diffObject, publishRef } from '../utils/rxjs.utils';
import { isExists, isNotExists } from '../utils/is.utils';
import { getQueryRequestSourceData } from '../utils/journal-engine.utils';

@UntilDestroy()
@Injectable()
export class JournalEngine<T extends JournalState = JournalState> {
  private readonly state$ = this.stateManager.getState().pipe(
    publishRef,
    // publishReplay(1),
    // refCount(),
  );

  private readonly displayingColumns$: Observable<ColumnParams[]>;
  private readonly records$: Observable<JournalRecord[]>;
  private readonly hasErrors$: Observable<boolean>;

  constructor(
    @Inject(JOURNAL_SERVICE) private service: JournalService,
    @Inject(JOURNAL_FACTORY) private factory: IJournalFactory,
    @Inject(JOURNAL_STATE_STORE) private stateManager: JournalStateManager,
  ) {
    // наблюдение за пераметрами отвечающими за изменения данных состояния отвечающих за получение информации о журнале `journalCode` и `isNeedToLoadJournalSettings`
    this.state$
      .pipe(
        distinctUntilChanged(),
        // ожидание изменения значений
        diffObject('journalCode', 'isNeedToLoadJournalSettings'),
        filter(
          ({ newState, diff }) =>
            isExists(newState.journalCode) &&
            (isNotExists(diff.isNeedToLoadJournalSettings) ||
              newState.isNeedToLoadJournalSettings ||
              Object.keys(diff).length > 1),
        ),
        // обращение к сервису(фасаду) поставщику данных для журнала
        switchMap(({ newState }) => {
          return this.service.getJournalByCode(newState.journalCode!);
        }),
        // формирование действия по внесению изменений в состояние с данными настроек журнала
        map(
          (settings) =>
            ({
              type: JournalActionTypes.SetJournalStaticSettings,
              payload: settings,
            } as SetJournalStaticSettingsAction),
        ),
        untilDestroyed(this),
      )
      .subscribe((action) => {
        this.stateManager.dispatch(action);
      });

    // NOTE: наблюдение за изменением состояния параметров влияющих на запрос(обновление) данных из сервиса данных
    const newQueryRequestParams$: Observable<any | QueryRequestSourceData> = this.state$.pipe(
      filter((state) => state.isReadyToLoadRecords),
      // ожидание изменения значений
      diffObject<JournalState>('journalCode', 'searchText', 'displayingColumns', 'isNeedToLoadRecords'),
      filter(
        ({ diff }) =>
          isNotExists(diff.isNeedToLoadRecords) ||
          (diff.isNeedToLoadRecords as FieldDiff<boolean>).newValue ||
          Object.keys(diff).length > 1,
      ),
      scan<DiffWithState<JournalState>, QueryRequestSourceData>(
        (prevData, diff) => getQueryRequestSourceData(prevData, diff, this.factory),
        null as unknown as QueryRequestSourceData,
      ),
      filter((val) => val !== null),
      distinctUntilChanged(),
      map(this.factory.makeQueryRequestConfig),
      publishRef,
    );

    newQueryRequestParams$
      .pipe(
        switchMap((queryParams: QueryRequestConfig) => {
          return this.service.fetchQuery(queryParams).pipe(
            catchError((err) => {
              return of({
                recordList: [],
                rowsCount: 0,
                hasErrors: true,
              } as JournalQueryResult);
            }),
          );
        }),
        map((result) => {
          return {
            type: JournalActionTypes.SetQueryResult,
            payload: result,
          } as SetQueryResultAction;
        }),
        untilDestroyed(this),
      )
      .subscribe((action: any) => {
        this.stateManager.dispatch(action);
      });

    this.displayingColumns$ = this.state$.pipe(pluck('displayingColumns'), distinctUntilChanged(), publishRef);
    this.hasErrors$ = this.state$.pipe(pluck('hasErrors'), distinctUntilChanged(), publishRef);
    this.records$ = this.state$.pipe(pluck('records'), distinctUntilChanged(), publishRef);
  }

  getState(): Observable<JournalState> {
    return this.stateManager.getState();
  }

  setJournalCode(code: string) {
    const action: SetJournalCodeAction = { type: JournalActionTypes.SetJournalCode, payload: code };
    this.stateManager.dispatch(action);
  }

  getHasErrors(): Observable<boolean> {
    return this.hasErrors$;
  }

  getRecords(): Observable<JournalRecord[]> {
    return this.records$;
  }

  // перезапросить данные (QueryResult) для журнала при текущем состоянии параметров запроса
  refreshResult(): void {
    const action: RefreshQueryResultAction = { type: JournalActionTypes.RefreshQueryResult };
    this.stateManager.dispatch(action);
  }

  getDisplayingColumns(): Observable<ColumnParams[]> {
    return this.displayingColumns$;
  }

  setSearchText(value: string) {
    const action: SetSearchTextAction = { type: JournalActionTypes.SetSearchText, payload: value };
    this.stateManager.dispatch(action);
  }
}
